'use client';

import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';

import { useCurrentUser } from '@website/hooks/useCurrentUser';

interface UserBookingHistoryProps {
  activityId: string;
}

/**
 * Shell component for the activity-page booking history.
 *
 * Anonymous viewers (the dominant Lighthouse case) hit the early `return null`
 * and never trigger the dynamic import. That keeps `bookings.service.ts`,
 * `@website/utils/timezone`, and Luxon (~26 KiB) entirely out of the page's
 * JavaScript graph — Lighthouse was previously flagging that chunk as 83 %
 * unused.
 *
 * Authenticated viewers see the same UI as before; the dynamic import resolves
 * within a microtask after `useCurrentUser` returns, so there's no perceived
 * delay for the people who actually need it.
 *
 * Mirrors the LocationSectionShell pattern.
 */
export default function UserBookingHistory({ activityId }: UserBookingHistoryProps) {
  const { data: user } = useCurrentUser();
  const [Impl, setImpl] = useState<ComponentType<UserBookingHistoryProps> | null>(null);

  useEffect(() => {
    if (!user || Impl) return;
    void import(/* webpackPrefetch: false, webpackPreload: false */ './UserBookingHistoryImpl').then((m) =>
      setImpl(() => m.default)
    );
  }, [user, Impl]);

  if (!user || !Impl) return null;
  return <Impl activityId={activityId} />;
}
